import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Alert,
  CircularProgress 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { createVenue } from '../../services/venueService';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import VenueBasicInfoForm from './components/VenueBasicInfoForm';
import VenueContactInfoForm from './components/VenueContactInfoForm';
import VenueLocationForm from './components/VenueLocationForm';
import VenueImagesForm from './components/VenueImagesForm';
import { VenueFormData, VenueType, BusinessHours } from '../../types/Venue';

// Default business hours
const DEFAULT_BUSINESS_HOURS: BusinessHours[] = [
  { dayOfWeek: 0, open: '10:00', close: '22:00', isClosed: false }, // Sunday
  { dayOfWeek: 1, open: '10:00', close: '22:00', isClosed: false }, // Monday
  { dayOfWeek: 2, open: '10:00', close: '22:00', isClosed: false }, // Tuesday
  { dayOfWeek: 3, open: '10:00', close: '22:00', isClosed: false }, // Wednesday
  { dayOfWeek: 4, open: '10:00', close: '22:00', isClosed: false }, // Thursday
  { dayOfWeek: 5, open: '10:00', close: '23:00', isClosed: false }, // Friday
  { dayOfWeek: 6, open: '10:00', close: '23:00', isClosed: false }, // Saturday
];

const steps = ['Basic Information', 'Contact Details', 'Location', 'Images'];

const CreateVenuePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [newVenueId, setNewVenueId] = useState<string | null>(null);
  
  // Form state
  const [venueData, setVenueData] = useState<VenueFormData>({
    name: '',
    type: 'restaurant' as VenueType,
    description: '',
    contact: {
      email: currentUser?.email || '',
      phone: '',
      website: ''
    },
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    location: {
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'USA',
      coordinates: {
        latitude: 0,
        longitude: 0
      }
    },
    amenities: [],
    businessHours: DEFAULT_BUSINESS_HOURS,
    images: {
      primary: '',
      gallery: [],
      logo: ''
    }
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const updateVenueData = (data: Partial<VenueFormData>) => {
    setVenueData(prevData => ({
      ...prevData,
      ...data
    }));
  };

  const handleSubmit = async () => {
    if (!currentUser?.uid) {
      setError('You must be logged in to create a venue');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the service to create the venue
      const venueId = await createVenue(currentUser.uid, venueData);
      setNewVenueId(venueId);
      setSuccess(true);
      
      // Redirect after a short delay to let the user see the success message
      setTimeout(() => {
        navigate('/venues');
      }, 1500);
    } catch (err) {
      console.error('Error creating venue:', err);
      setError('Failed to create venue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <VenueBasicInfoForm 
                 venueData={venueData} 
                 updateVenueData={updateVenueData} 
               />;
      case 1:
        return <VenueContactInfoForm 
                 venueData={venueData} 
                 updateVenueData={updateVenueData} 
               />;
      case 2:
        return <VenueLocationForm 
                 venueData={venueData} 
                 updateVenueData={updateVenueData} 
               />;
      case 3:
        return <VenueImagesForm 
                 venueData={venueData} 
                 updateVenueData={updateVenueData}
                 venueId={newVenueId}
               />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Create a New Venue
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success ? (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Venue created successfully! Redirecting you to the venues page...
          </Alert>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/venues')}
            >
              Go to Venues List
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Create a venue to manage packages, bookings, and promote your business. Venues can offer date packages, event spaces, and more.
          </Typography>

          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            
            <Box sx={{ mt: 2, mb: 2 }}>
              {getStepContent(activeStep)}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
              <Button
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              
              <div>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} color="inherit" />}
                  >
                    {loading ? 'Creating Venue...' : 'Create Venue'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                )}
              </div>
            </Box>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default CreateVenuePage;
